'use client';

import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useSearchParams } from 'next/navigation';
import { useAccount } from 'wagmi';

import { ConnectConsole } from '@web/features/staking/ConnectConsole';
import { useDeposits, useRewardsOf } from '@web/features/staking/hooks';
import { StakingPersonas } from '@web/features/staking/StakingPersonas';
import { StakingPersonasNotConnected } from '@web/features/staking/StakingPersonas/StakingPersonasNotConnected';
import { StakingTerminal } from '@web/features/staking/StakingTerminal';
import { useCurrentCompletedUser, useCurrentUser } from '@web/features/user/service';
import { useAuth } from '@web/utils/auth/manager';
import { env } from '@web/utils/env';

const StakingPageContainer = styled.main`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('3000')};
  padding-inline: ${({ theme }) => theme.spacing('0500')};
  padding-bottom: ${({ theme }) => theme.spacing('3000')};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: min(100%, 1800px);
    box-sizing: border-box;
    margin-inline: auto;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding-bottom: 80px;
    padding-inline: ${({ theme }) => theme.spacing('1000')};
  }
`;

const DebugOverlay = styled.pre`
  position: fixed;
  top: 80px;
  right: 12px;
  z-index: 9999;
  max-width: 480px;
  max-height: 80vh;
  overflow: auto;
  padding: 12px 16px;
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.4;
  background: rgba(0, 0, 0, 0.92);
  color: #00ff66;
  border: 1px solid #00ff66;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-all;
`;

export default function StakingPage() {
  const completedUser = useCurrentCompletedUser();
  const currentUser = useCurrentUser();
  const wallet = useAccount();
  const auth = useAuth();
  const isWalletConnected = wallet.status === 'connected';
  const { data: depositsData, error: depositsErr, isLoading: depositsLoading } =
    useDeposits({ enabled: true });
  const { data: rewardsOfData, error: rewardsErr, isLoading: rewardsLoading } =
    useRewardsOf({ enabled: true });
  const dataLoading = depositsData === undefined || rewardsOfData === undefined;
  const isConnected = completedUser && isWalletConnected && !dataLoading;

  const searchParams = useSearchParams();
  const debug = searchParams.get('debug') === '1';

  const [liveAccounts, setLiveAccounts] = useState<string[] | 'pending' | 'no-ethereum'>(
    'pending',
  );
  useEffect(() => {
    if (!debug) return;
    const eth = (globalThis as { ethereum?: { request?: (a: { method: string }) => Promise<unknown> } }).ethereum;
    if (!eth?.request) { setLiveAccounts('no-ethereum'); return; }
    let cancelled = false;
    eth.request({ method: 'eth_accounts' }).then((r) => {
      if (cancelled) return;
      setLiveAccounts(Array.isArray(r) ? (r as string[]) : []);
    }).catch(() => { if (!cancelled) setLiveAccounts([]); });
    return () => { cancelled = true; };
  }, [debug, wallet.address, wallet.status]);

  // RPC ping test: hits each configured transport URL with eth_blockNumber.
  // Tells us whether the RPC endpoint is reachable & fast, without going
  // through wagmi. If wagmi's useReadContract hangs but this ping succeeds,
  // the bug is in wagmi/contract; if this ping also hangs, the RPC is the
  // problem and we should switch transports.
  type RpcResult =
    | { ok: true; blockNumber: string; ms: number }
    | { ok: false; error: string; ms: number }
    | 'pending';
  const [ethRpcResult, setEthRpcResult] = useState<RpcResult>('pending');
  const [baseRpcResult, setBaseRpcResult] = useState<RpcResult>('pending');
  const [baseDepositsCallResult, setBaseDepositsCallResult] = useState<
    | { ok: true; resultLen: number; ms: number; preview: string }
    | { ok: false; error: string; ms: number }
    | 'pending'
  >('pending');

  useEffect(() => {
    if (!debug) return;
    const ping = async (url: string | undefined, set: (r: RpcResult) => void) => {
      if (!url) { set({ ok: false, error: 'no URL configured (using viem default)', ms: 0 }); return; }
      const t0 = performance.now();
      try {
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'eth_blockNumber', params: [] }),
        });
        const ms = Math.round(performance.now() - t0);
        const body = await r.json();
        if (body?.result) set({ ok: true, blockNumber: body.result, ms });
        else set({ ok: false, error: JSON.stringify(body).slice(0, 120), ms });
      } catch (e) {
        set({ ok: false, error: String((e as Error).message ?? e), ms: Math.round(performance.now() - t0) });
      }
    };
    ping(env.ethereum.RPC_HTTP, setEthRpcResult);
    ping(env.base.RPC_HTTP, setBaseRpcResult);

    // Replicate wagmi's depositsOf(wallet) call directly against Base RPC.
    // Function selector for depositsOf(address) = 0xe3a9db1a (known from
    // the failing useReadContract earlier). If this succeeds, wagmi must be
    // misconfigured. If this hangs/fails, the RPC can't service eth_call.
    const callDeposits = async () => {
      const url = env.base.RPC_HTTP;
      if (!url || !wallet.address) {
        setBaseDepositsCallResult({ ok: false, error: 'no RPC URL or wallet address', ms: 0 });
        return;
      }
      const t0 = performance.now();
      try {
        const addrLower = wallet.address.toLowerCase().replace(/^0x/, '');
        const data = '0xe3a9db1a' + addrLower.padStart(64, '0');
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0', id: 2, method: 'eth_call',
            params: [{ to: env.base.STAKING_ADDRESS, data }, 'latest'],
          }),
        });
        const ms = Math.round(performance.now() - t0);
        const body = await r.json();
        if (body?.result) {
          setBaseDepositsCallResult({
            ok: true,
            resultLen: (body.result as string).length,
            ms,
            preview: (body.result as string).slice(0, 100),
          });
        } else {
          setBaseDepositsCallResult({ ok: false, error: JSON.stringify(body).slice(0, 200), ms });
        }
      } catch (e) {
        setBaseDepositsCallResult({
          ok: false,
          error: String((e as Error).message ?? e),
          ms: Math.round(performance.now() - t0),
        });
      }
    };
    callDeposits();
  }, [debug, wallet.address]);

  return (
    <StakingPageContainer>
      {debug && (
        <DebugOverlay>
          {JSON.stringify(
            {
              gates: {
                isConnected: !!isConnected,
                'completedUser (truthy)': !!completedUser,
                isWalletConnected,
                dataLoading,
                renders: isConnected ? 'StakingTerminal' : 'ConnectConsole',
              },
              auth: {
                hasJwt: !!auth,
                access: auth?.access?.slice(0, 20) + '...',
                accessExpiresAt: auth?.accessExpiresAt,
              },
              currentUser: {
                status: currentUser.status,
                hasTwitter: currentUser.status === 'connected' ? !!currentUser.user.twitter : null,
                twitterHandle:
                  currentUser.status === 'connected'
                    ? currentUser.user.twitter?.handle ?? null
                    : null,
                walletsCount:
                  currentUser.status === 'connected' ? currentUser.user.wallets.length : null,
                personasCount:
                  currentUser.status === 'connected' ? currentUser.user.personas.length : null,
              },
              wallet: {
                status: wallet.status,
                address: wallet.address,
                chainId: wallet.chainId,
                connectorName: wallet.connector?.name,
              },
              liveEthAccounts: liveAccounts,
              rpc: {
                ethereumUrl: env.ethereum.RPC_HTTP ?? '(viem default)',
                baseUrl: env.base.RPC_HTTP ?? '(viem default)',
                ethereumPing: ethRpcResult,
                basePing: baseRpcResult,
                baseDepositsCallDirect: baseDepositsCallResult,
              },
              deposits: {
                isLoading: depositsLoading,
                hasData: depositsData !== undefined,
                tokenIdsCount: depositsData?.[0]?.length ?? null,
                error: depositsErr ? String(depositsErr.message ?? depositsErr) : null,
              },
              rewardsOf: {
                isLoading: rewardsLoading,
                hasData: rewardsOfData !== undefined,
                keys: rewardsOfData ? Object.keys(rewardsOfData).length : null,
                error: rewardsErr ? String(rewardsErr.message ?? rewardsErr) : null,
              },
            },
            null,
            2,
          )}
        </DebugOverlay>
      )}
      {isConnected ? <StakingTerminal /> : <ConnectConsole />}
      {isConnected ? <StakingPersonas /> : <StakingPersonasNotConnected />}
    </StakingPageContainer>
  );
}
